import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, ChevronRight, Lock, Save, Send, Undo2 } from 'lucide-react';
import { Button } from '../../Button';
import { Panel } from '../../common/Panel';
import { NativeSelect } from '../../common/NativeSelect';
import { ApprovalPanel } from './ApprovalPanel';
import { useOwnerService } from '../../../contexts/OwnerServiceContext';
import { useApplicationReview } from '../../../contexts/ApplicationReviewContext';
import { validationOfficers } from '../../../data/applicationReview';
import { can, queueOf } from '../../../utils/applicationQueue';
import { validationItems } from '../../../utils/applicationRecords';
import { toBn } from '../../../utils/bn';
import type { OwnerApplication } from '../../../types/owner';
import type { DetailTab, ValidationForm, ValidationResult } from '../../../types/review';
interface Props {
  app: OwnerApplication;
  goTab: (t: DetailTab) => void;
  onReturn: () => void;
  onReject: () => void;
}
const results: ValidationResult[] = ['তথ্য সঠিক', 'সংশোধন প্রয়োজন', 'তথ্যের অমিল'];
export function ValidationTab({
  app,
  goTab,
  onReturn,
  onReject
}: Props) {
  const {
    applications
  } = useOwnerService();
  const {
    getReview,
    saveValidation,
    sendForApproval,
    role,
    user,
    driverIdsFor
  } = useApplicationReview();
  const r = getReview(app);
  const items = validationItems(app, r, applications, driverIdsFor(app));
  const done = items.filter(i => i.done).length;
  const complete = done === items.length;
  const [form, setForm] = useState<ValidationForm>(r.validation);
  const q = queueOf(app);
  const canValidate = can(role, 'validate');
  const inValidation = q === 'new' || q === 'processing';
  useEffect(() => {
    setForm({
      ...r.validation,
      officer: r.validation.officer || user.user
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.id, app.stage]);
  const set = <K extends keyof ValidationForm,>(k: K, v: ValidationForm[K]) => setForm(f => ({
    ...f,
    [k]: v
  }));
  const save = () => {
    saveValidation(app, form);
    toast.success('যাচাইয়ের তথ্য সংরক্ষণ করা হয়েছে');
  };
  const send = () => {
    if (!complete) return toast.error('সকল যাচাই সম্পন্ন না হওয়া পর্যন্ত অনুমোদনের জন্য পাঠানো যাবে না');
    if (form.result !== 'তথ্য সঠিক') return toast.error('ফলাফল “তথ্য সঠিক” হলে কেবল অনুমোদনের জন্য পাঠানো যাবে');
    if (app.stage < 2) return toast.error('প্রথমে “যাচাই শুরু করুন”');
    sendForApproval(app, form);
    toast.success('অনুমোদনকারী কর্মকর্তার কাছে পাঠানো হয়েছে');
  };
  return <div className="space-y-4" data-id="element-621" data-mp="3v9qoju6yg0l1">
      <div className="grid gap-4 xl:grid-cols-5" data-id="element-622" data-mp="3gjjfu66r1vxj">
        <Panel title="আবেদন যাচাই কার্যক্রম" subtitle="প্রতিটি বিষয়ে ক্লিক করে সংশ্লিষ্ট ট্যাবে যান" className="xl:col-span-2" data-id="element-623" data-mp="3giq6qykbiuc8">
          <div className="mb-4 flex items-center gap-3" data-id="element-624" data-mp="d2jctjrpgbgn">
            <div className="relative h-16 w-16 shrink-0" data-id="element-625" data-mp="d0q1hr65d32b">
              <svg viewBox="0 0 36 36" className="h-16 w-16 -rotate-90" data-id="element-626" data-mp="htj5gctsk7dc">
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e2e8f0" strokeWidth="3.5" data-id="element-627" data-mp="22jev18gpuf57" />
                <circle cx="18" cy="18" r="15.5" fill="none" stroke={complete ? '#059669' : '#003B71'} strokeWidth="3.5" strokeDasharray={`${done / items.length * 97.4} 97.4`} strokeLinecap="round" data-id="element-628" data-mp="1xkbr5rch2eyt" />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center font-heading text-sm font-bold text-navy-900" data-id="element-629" data-mp="279lk19f541nz">{toBn(done)}/{toBn(items.length)}</span>
            </div>
            <div data-id="element-630" data-mp="1elpo46xeads9">
              <p className="font-heading text-lg font-bold text-navy-900" data-id="element-631" data-mp="1em977qys787v">{toBn(done)}/{toBn(items.length)} যাচাই সম্পন্ন</p>
              <p className="text-xs text-slate-500" data-id="element-632" data-mp="1sm0fdb2m9udy">{complete ? 'সকল যাচাই সম্পন্ন — অনুমোদনের জন্য পাঠানো যাবে' : 'অসম্পূর্ণ বিষয়গুলো সম্পন্ন করুন'}</p>
            </div>
          </div>
          <ul className="space-y-1.5" data-id="element-633" data-mp="33zcoo1h5b57i">
            {items.map(i => <li key={i.label} data-id="element-634" data-mp="2zz2gci5vs2ph">
                <button onClick={() => goTab(i.tab)} className={`flex w-full items-center gap-2.5 rounded-md border px-3 py-2 text-left text-sm transition hover:border-navy-300 ${i.done ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200'}`} data-id="element-635" data-mp="2zy0tzhjeztsg">
                  {i.done ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" data-id="element-636" data-mp="84j4twwcuk3d" /> : <span className="h-4 w-4 shrink-0 rounded-full border-2 border-slate-300" data-id="element-637" data-mp="86cell29lkfl" />}
                  <span className="flex-1" data-id="element-638" data-mp="d4np7y0dvyyv">
                    <span className="block font-medium text-navy-900" data-id="element-639" data-mp="i2yxi4b161ez">{i.label}</span>
                    {i.note && <span className={`block text-[11px] ${i.done ? 'text-slate-500' : 'text-red-600'}`} data-id="element-640" data-mp="m3itv5ns5102">{i.note}</span>}
                  </span>
                  <ChevronRight className="h-4 w-4 text-slate-300" data-id="element-641" data-mp="qvamdlqhxgsu" />
                </button>
              </li>)}
          </ul>
        </Panel>

        <Panel title="যাচাইকারী কর্মকর্তার ফর্ম" subtitle={r.validation.saved ? 'সর্বশেষ যাচাই সংরক্ষিত' : 'এখনো সংরক্ষিত হয়নি'} className="xl:col-span-3" data-id="element-642" data-mp="10tgn400kggw4">
          {!canValidate && <p className="mb-3 flex items-center gap-1.5 rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-600" data-id="element-643" data-mp="15pnmtb2dcw5m"><Lock className="h-3.5 w-3.5" data-id="element-644" data-mp="2laz9wfkn456h" />কেবল যাচাই কর্মকর্তা এই ফর্ম সম্পাদনা ও অনুমোদনের জন্য পাঠাতে পারেন।</p>}
          <fieldset disabled={!canValidate || !inValidation} className="space-y-3.5 disabled:opacity-80" data-id="element-645" data-mp="2gbmlvri18s2b">
            <div className="grid gap-3 sm:grid-cols-2" data-id="element-646" data-mp="2bdbctizkall5">
              <NativeSelect id="vf-off" label="যাচাইকারী কর্মকর্তা" options={Array.from(new Set([form.officer, ...validationOfficers]))} value={form.officer} onChange={e => set('officer', e.target.value)} data-id="element-647" data-mp="2bewa3gd9gimp" />
              <div data-id="element-648" data-mp="343tf4hsgoixi">
                <label htmlFor="vf-date" className="mb-1 block text-xs font-medium text-slate-600" data-id="element-649" data-mp="2zah5bdsfygot">যাচাইয়ের তারিখ</label>
                <input id="vf-date" value={form.date} onChange={e => set('date', e.target.value)} className="h-9 w-full rounded-md border border-input px-3 text-sm outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100" data-id="element-650" data-mp="2ucfhygnzfv7j" />
              </div>
            </div>
            <div data-id="element-651" data-mp="1l318dct23bht">
              <label htmlFor="vf-obs" className="mb-1 block text-xs font-medium text-slate-600" data-id="element-652" data-mp="1l2j1lm8rpevn">পর্যবেক্ষণ</label>
              <textarea id="vf-obs" rows={3} value={form.observation} onChange={e => set('observation', e.target.value)} placeholder="যাচাইকালে পাওয়া গুরুত্বপূর্ণ পর্যবেক্ষণ" className="w-full rounded-md border border-input px-3 py-2 text-sm outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100" data-id="element-653" data-mp="1q0ucm0astvfg" />
            </div>
            <div data-id="element-654" data-mp="12ee4gxmm8s5t">
              <label htmlFor="vf-cmt" className="mb-1 block text-xs font-medium text-slate-600" data-id="element-655" data-mp="16dwo7q2pax72">মন্তব্য</label>
              <textarea id="vf-cmt" rows={2} value={form.comment} onChange={e => set('comment', e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100" data-id="element-656" data-mp="sedfow5z9ju8" />
            </div>
            <div data-id="element-657" data-mp="2mq1xr77ia2iu">
              <p className="mb-1.5 text-xs font-medium text-slate-600" data-id="element-658" data-mp="2rr7s19gtdxuh">যাচাইয়ের ফলাফল</p>
              <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="যাচাইয়ের ফলাফল" data-id="element-659" data-mp="2rovd756cs3ln">
                {results.map(res => <button type="button" key={res} role="radio" aria-checked={form.result === res} onClick={() => set('result', res)} className={`rounded-md border px-2 py-2 text-[13px] font-semibold ${form.result === res ? res === 'তথ্য সঠিক' ? 'border-emerald-600 bg-emerald-600 text-white' : res === 'সংশোধন প্রয়োজন' ? 'border-amber-500 bg-amber-500 text-white' : 'border-red-600 bg-red-600 text-white' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`} data-id="element-660" data-mp="b5ufdqed68in">
                    {res}
                  </button>)}
              </div>
            </div>
          </fieldset>
          {inValidation && <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4" data-id="element-661" data-mp="35ktmpkdtpezz">
              <Button variant="outline" onClick={onReturn} disabled={!can(role, 'return')} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-id="element-662" data-mp="2jrnwgt8lessm"><Undo2 className="h-4 w-4" data-id="element-663" data-mp="2oppgphhr8y5o" />সংশোধনের জন্য ফেরত</Button>
              <Button variant="outline" onClick={save} disabled={!canValidate} data-id="element-664" data-mp="2thpofw42gvct"><Save className="h-4 w-4" data-id="element-665" data-mp="2tjk8z9bfelgd" />যাচাই সংরক্ষণ</Button>
              <Button onClick={send} disabled={!canValidate || !complete || form.result !== 'তথ্য সঠিক'} data-id="element-666" data-mp="2yfiriga2p98v"><Send className="h-4 w-4" data-id="element-667" data-mp="20tlgf3y0w66y" />অনুমোদনের জন্য পাঠান</Button>
            </div>}
          {!inValidation && <p className="mt-4 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500" data-id="element-668" data-mp="25soajtcgss05">এই আবেদন যাচাই ধাপ অতিক্রম করেছে — ফর্মটি কেবল দেখার জন্য।</p>}
        </Panel>
      </div>

      <ApprovalPanel app={app} onReturn={onReturn} onReject={onReject} validationComplete={complete} data-id="element-669" data-mp="2estfzxy3lgbo" />
    </div>;
}